import { useMemo } from 'react';
import { XIcon } from 'lucide-react';

import { FLOATING_OVERLAY_DEFAULT_INSET_PX } from '@/shared/config';
import { Button } from '@/shared/ui/shadcn/button';
import {
  FloatingOverlayPortal,
  OverlayPanel,
  type Position,
  type Size,
} from '@/shared/ui/primitives';

import { COPILOT_OVERLAY_DEFINITIONS } from './model/overlayDefinitions';
import type { CopilotOverlayId } from './model/types';

export interface CopilotOverlayOutletProps {
  activeOverlayIds: CopilotOverlayId[];
  userId: string;
  onClose: (id?: CopilotOverlayId) => void;
}

function getBottomRightPosition(size: Size, insetPx: number): Position {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  const widthPx = typeof size.width === 'number' ? size.width : 0;
  const heightPx = typeof size.height === 'number' ? size.height : 0;

  return {
    x: Math.max(insetPx, window.innerWidth - widthPx - insetPx),
    y: Math.max(insetPx, window.innerHeight - heightPx - insetPx),
  };
}

export function CopilotOverlayOutlet({
  activeOverlayIds,
  userId,
  onClose,
}: CopilotOverlayOutletProps) {
  const overlays = useMemo(
    () => activeOverlayIds.map((id) => COPILOT_OVERLAY_DEFINITIONS[id]),
    [activeOverlayIds]
  );

  const defaultPositions = useMemo(() => {
    if (overlays.length === 0) return [];
    let nextX: number | undefined;

    return overlays.map((overlay) => {
      const base = getBottomRightPosition(overlay.defaultSize, FLOATING_OVERLAY_DEFAULT_INSET_PX);
      const x = nextX ?? base.x;
      nextX =
        x - (typeof overlay.defaultSize.width === 'number' ? overlay.defaultSize.width : 0) - 16;
      return { x, y: base.y };
    });
  }, [overlays]);

  if (overlays.length === 0 || defaultPositions.length === 0) {
    return null;
  }

  return (
    <FloatingOverlayPortal>
      {overlays.map((overlay, index) => (
        <OverlayPanel
          key={overlay.id}
          bounds="window"
          collapsible
          defaultPosition={defaultPositions[index]}
          defaultSize={overlay.defaultSize}
          headerIcon={overlay.headerIcon}
          headerIconLabel={overlay.headerIconLabel}
          actions={
            <Button
              aria-label="Close"
              size="icon-sm"
              type="button"
              variant="ghost"
              onClick={() => onClose(overlay.id)}
            >
              <XIcon className="size-4" />
            </Button>
          }
        >
          {overlay.render(userId)}
        </OverlayPanel>
      ))}
    </FloatingOverlayPortal>
  );
}
